/**
 * AdminSettingsPage - /admin/settings. Phase 13 frontend.
 *
 * Closes gap G7 from the 2026-05-11 Phase 17 audit: the backend
 * system_settings CRUD shipped in commit 761db02, but until this page
 * there was no UI to edit the rows or to navigate to the
 * per-component admin pages.
 *
 * SCOPE TRADEOFF. SPEC sec 9 AdminSettingsPage describes per-integration
 * cards (Microsoft 365 mail, Buyist API, AI provider, etc.) that hide
 * the underlying system_settings rows behind a friendlier surface. This
 * branch is deliberately NARROWER: a generic row editor + a navigation
 * hub. The per-integration cards land in a Phase 13.2 follow-up. The
 * header copy is the place where this tradeoff is made visible to a
 * future contributor.
 */
import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { EmailSendRulesCard } from "@/components/admin/EmailSendRulesCard";
import { SlackAlertCard } from "@/components/admin/SlackAlertCard";
import { AiAutosendCard } from "@/components/admin/AiAutosendCard";
import { ReplySignatureCard } from "@/components/admin/ReplySignatureCard";

import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
} from "@/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useAdminSettings,
  useDeleteSetting,
  useRevealSetting,
  useUpsertSetting,
} from "@/hooks/useAdminSettings";
import type { SystemSettingResponse } from "@/types/api";

// ---------------------------------------------------------------------------
// Quick links to the existing admin sub-pages.
// ---------------------------------------------------------------------------

interface QuickLink {
  title: string;
  description: string;
  icon: string;
  to: string;
}

const QUICK_LINKS: QuickLink[] = [
  {
    title: "Users",
    description: "Manage accounts and roles across all companies.",
    icon: "fa-duotone fa-thin fa-users",
    to: "/admin/users",
  },
  {
    title: "Webhooks",
    description: "Register outbound endpoints and replay deliveries.",
    icon: "fa-duotone fa-thin fa-bolt",
    to: "/admin/webhooks",
  },
  {
    title: "Health",
    description: "Per-component reachability of every dependency.",
    icon: "fa-duotone fa-thin fa-heart-pulse",
    to: "/admin/health",
  },
  {
    title: "Logs",
    description: "Live tail of system_log with filters.",
    icon: "fa-duotone fa-thin fa-file-lines",
    to: "/admin/logs",
  },
  {
    title: "Spam blocklist",
    description: "Sender patterns that bypass ticket creation.",
    icon: "fa-duotone fa-thin fa-shield-xmark",
    to: "/admin/spam",
  },
  {
    title: "Sender rewrites",
    description:
      "Reroute inbound From: addresses (e.g. operator@ to datacontrol@).",
    icon: "fa-duotone fa-thin fa-arrow-right-arrow-left",
    to: "/admin/sender-rewrites",
  },
  {
    title: "Import status",
    description: "Freshdesk, Gemini, inbound email, Buyist API sync.",
    icon: "fa-duotone fa-thin fa-cloud-arrow-down",
    to: "/admin/import-status",
  },
  {
    title: "Audit",
    description: "Cross-ticket audit search over ticket_events.",
    icon: "fa-duotone fa-thin fa-magnifying-glass",
    to: "/admin/audit",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KEY_REGEX = /^[a-z0-9_.\-]+$/i;

function extractErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as {
    response?: { data?: { detail?: string } };
  })?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

/**
 * Render the cached value preview shown in the row.
 *
 * For secret rows the backend already returns a masked string (e.g.
 * "***1234"); we render that verbatim. For non-secret rows we
 * stringify the polymorphic JSON value and truncate to 80 chars so a
 * single very long blob can't blow up the table layout.
 */
function valuePreview(row: SystemSettingResponse): string {
  if (row.is_secret) {
    if (typeof row.value === "string") return row.value;
    return "***";
  }
  let s: string;
  try {
    s = typeof row.value === "string"
      ? row.value
      : JSON.stringify(row.value);
  } catch {
    s = String(row.value);
  }
  if (s === undefined) return "";
  return s.length > 80 ? `${s.slice(0, 80)}...` : s;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Best-effort copy. Falls back to a no-op when the clipboard API is
 * missing (which is the default under jsdom in tests). */
async function copyToClipboard(value: string): Promise<boolean> {
  const cb = (
    globalThis as { navigator?: { clipboard?: { writeText?: (s: string) => Promise<void> } } }
  ).navigator?.clipboard;
  if (cb && typeof cb.writeText === "function") {
    try {
      await cb.writeText(value);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Pre-stringify a value for the edit textarea. We always show JSON so
 * the round-trip is lossless: a string "hello" becomes ``"hello"`` and
 * a number 42 stays ``42``. Booleans, null, arrays, and objects all
 * render the same way the backend stored them.
 */
function valueToJsonString(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// ---------------------------------------------------------------------------
// Edit / create modal - shared shape.
// ---------------------------------------------------------------------------

interface EditorState {
  /** ``null`` while in create mode; the existing row when editing. */
  existing: SystemSettingResponse | null;
  key: string;
  valueText: string;
  isSecret: boolean;
  description: string;
  category: string;
}

function emptyEditor(): EditorState {
  return {
    existing: null,
    key: "",
    valueText: '""',
    isSecret: false,
    description: "",
    category: "",
  };
}

function editorForRow(row: SystemSettingResponse): EditorState {
  return {
    existing: row,
    key: row.key,
    valueText: valueToJsonString(row.value),
    isSecret: row.is_secret,
    description: row.description ?? "",
    category: row.category ?? "",
  };
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function AdminSettingsPage(): JSX.Element {
  usePageTitle("Settings");

  const settingsQuery = useAdminSettings();
  const upsertSetting = useUpsertSetting();
  const deleteSetting = useDeleteSetting();
  const revealSetting = useRevealSetting();

  const rows = useMemo(
    () => settingsQuery.data ?? [],
    [settingsQuery.data],
  );

  // Filter controls (client-side - the list is small enough).
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.category) set.add(r.category);
    }
    return Array.from(set).sort();
  }, [rows]);

  const categoryOptions = useMemo(
    () => [
      { value: "", label: "All categories" },
      ...categories.map((c) => ({ value: c, label: c })),
    ],
    [categories],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (categoryFilter && (r.category ?? "") !== categoryFilter) {
        return false;
      }
      if (q && !r.key.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, search, categoryFilter]);

  // Modals.
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorError, setEditorError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<SystemSettingResponse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [revealing, setRevealing] = useState<SystemSettingResponse | null>(
    null,
  );
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  // --- Editor ----------------------------------------------------------
  const openCreate = () => {
    setEditor(emptyEditor());
    setEditorMode("create");
    setEditorError(null);
  };

  const openEdit = (row: SystemSettingResponse) => {
    setEditor(editorForRow(row));
    setEditorMode("edit");
    setEditorError(null);
  };

  const closeEditor = () => {
    setEditor(null);
    setEditorError(null);
  };

  const handleEditorSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editor) return;
    setEditorError(null);

    const key = editor.key.trim();
    if (editorMode === "create") {
      if (!key) {
        setEditorError("Key is required.");
        return;
      }
      if (!KEY_REGEX.test(key)) {
        setEditorError(
          "Key may only contain letters, digits, dots, underscores, and hyphens.",
        );
        return;
      }
    }

    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(editor.valueText);
    } catch {
      setEditorError(
        "Invalid JSON - did you forget quotes for a string?",
      );
      return;
    }

    try {
      await upsertSetting.mutateAsync({
        key,
        body: {
          value: parsedValue,
          is_secret: editor.isSecret,
          description: editor.description.trim() || null,
          category: editor.category.trim() || null,
        },
      });
      closeEditor();
    } catch (err) {
      setEditorError(extractErrorMessage(err, "Could not save setting."));
    }
  };

  // --- Delete ----------------------------------------------------------
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteSetting.mutateAsync(pendingDelete.key);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Could not delete setting."));
    }
  };

  // --- Reveal ----------------------------------------------------------
  const openReveal = async (row: SystemSettingResponse) => {
    setRevealing(row);
    setRevealedValue(null);
    setRevealError(null);
    setCopyState("idle");
    try {
      const result = await revealSetting.mutateAsync(row.key);
      const cleartext =
        typeof result.value === "string"
          ? result.value
          : JSON.stringify(result.value);
      setRevealedValue(cleartext);
    } catch (err) {
      setRevealError(extractErrorMessage(err, "Could not reveal setting."));
    }
  };

  const closeReveal = () => {
    setRevealing(null);
    setRevealedValue(null);
    setRevealError(null);
    setCopyState("idle");
  };

  const handleCopyRevealed = async () => {
    if (revealedValue === null) return;
    const ok = await copyToClipboard(revealedValue);
    setCopyState(ok ? "copied" : "failed");
    window.setTimeout(() => setCopyState("idle"), 2000);
  };

  // -- Render -----------------------------------------------------------
  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-neutral-900">Settings</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Edit system_settings rows and navigate to the per-component
          admin pages. Per-integration cards (Microsoft 365 mail, Buyist
          API, AI provider, etc.) land in a follow-up branch.
        </p>
      </header>

      {/* ------------------------------------------------------------- */}
      {/* Section 1 - Email send rules                                   */}
      {/* ------------------------------------------------------------- */}
      {/* Sits ABOVE the reply signature because outbound-gate control
          is more critical to gate than the signature is to template:
          a wrong setting here can silently break customer email. */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Outbound email</h2>
        <EmailSendRulesCard />
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section 2 - Reply signature template                           */}
      {/* ------------------------------------------------------------- */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Agent reply signature</h2>
        <ReplySignatureCard />
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section 3 - Slack ticket alerts                                */}
      {/* ------------------------------------------------------------- */}
      {/* Optional outbound Slack notifications for new tickets, ticket
          assignments to Niki / Greg, and responses on their tickets.
          Empty URL = disabled (no webhook fires). See
          backend/app/services/slack_notification_service.py. */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Slack alerts</h2>
        <SlackAlertCard />
      </section>

      {/* AI autonomous replies (2026-09-26). Requester domains on which an
          AI key may send its own draft. Empty = off. See
          backend/app/services/ai_autosend_service.py. */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">
          AI autonomous replies
        </h2>
        <AiAutosendCard />
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section 2 - Quick links                                        */}
      {/* ------------------------------------------------------------- */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Quick links</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {QUICK_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block rounded-lg border border-neutral-200 bg-white p-4 shadow-sm transition-colors hover:bg-neutral-50"
            >
              <div className="flex items-start gap-3">
                <i
                  className={`${link.icon} shrink-0 text-2xl text-primary-600`}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-neutral-900">
                    {link.title}
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600">
                    {link.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Section 2 - system_settings row editor                         */}
      {/* ------------------------------------------------------------- */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Settings</h2>
            <p className="mt-1 text-sm text-neutral-600">
              Raw row editor for the system_settings table. Values are
              stored as JSONB - use JSON syntax for strings, numbers,
              booleans, arrays, and objects.
            </p>
          </div>
          <Button type="button" onClick={openCreate}>
            <i className="fa-duotone fa-thin fa-plus" aria-hidden="true" />
            Add setting
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            id="settings-search"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by key"
          />
          <Select
            id="settings-category-filter"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={categoryOptions}
            aria-label="Filter by category"
          />
        </div>

        {settingsQuery.isError && (
          <Alert variant="error" title="Could not load settings">
            Try refreshing the page. If the problem persists, check the
            backend logs.
          </Alert>
        )}

        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Category
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Key
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Value
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Updated
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {settingsQuery.isLoading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center">
                      <Spinner size="md" />
                    </td>
                  </tr>
                )}
                {!settingsQuery.isLoading && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-sm text-neutral-500"
                    >
                      No settings configured. Click Add setting to create one.
                    </td>
                  </tr>
                )}
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 text-sm text-neutral-600">
                      {row.category ?? "-"}
                    </td>
                    <td className="px-3 py-2 font-mono text-sm text-neutral-900">
                      {row.key}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <code className="break-all font-mono text-xs text-neutral-700">
                          {valuePreview(row)}
                        </code>
                        {row.is_secret && (
                          <Badge variant="warning">secret</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-neutral-600">
                      {row.description ?? "-"}
                    </td>
                    <td className="px-3 py-2 text-sm text-neutral-500">
                      {formatDate(row.updated_at)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm">
                      <div className="inline-flex items-center gap-2">
                        {row.is_secret && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              void openReveal(row);
                            }}
                          >
                            Reveal
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPendingDelete(row);
                            setDeleteError(null);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Create / edit modal                                            */}
      {/* ------------------------------------------------------------- */}
      <Modal
        isOpen={editor !== null}
        onClose={closeEditor}
        title={editorMode === "create" ? "Add setting" : "Edit setting"}
        size="lg"
      >
        {editor && (
          <form onSubmit={handleEditorSubmit} className="space-y-4">
            {editorMode === "create" && (
              <Input
                id="setting-key"
                label="Key"
                value={editor.key}
                onChange={(e) =>
                  setEditor({ ...editor, key: e.target.value })
                }
                placeholder="e.g. graph.mailbox_address"
                helperText="Letters, digits, dots, underscores, and hyphens only."
                required
              />
            )}
            {editorMode === "edit" && (
              <div>
                <span className="block text-sm font-medium text-neutral-700">
                  Key
                </span>
                <p className="mt-1 font-mono text-sm text-neutral-900">
                  {editor.key}
                </p>
              </div>
            )}
            <Textarea
              id="setting-value"
              label="Value"
              value={editor.valueText}
              onChange={(e) =>
                setEditor({ ...editor, valueText: e.target.value })
              }
              rows={6}
              helperText='JSON. Strings need quotes ("hello"), numbers do not (42).'
              required
            />
            <Input
              id="setting-description"
              label="Description"
              value={editor.description}
              onChange={(e) =>
                setEditor({ ...editor, description: e.target.value })
              }
              placeholder="What is this setting for?"
            />
            <Input
              id="setting-category"
              label="Category"
              value={editor.category}
              onChange={(e) =>
                setEditor({ ...editor, category: e.target.value })
              }
              placeholder="e.g. integrations"
            />
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={editor.isSecret}
                onChange={(e) =>
                  setEditor({ ...editor, isSecret: e.target.checked })
                }
                className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              Is secret (value will be masked on list and require reveal)
            </label>

            {editorError && (
              <Alert variant="error" title="Could not save">
                {editorError}
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditor}
                disabled={upsertSetting.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={upsertSetting.isPending}
              >
                Save
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ------------------------------------------------------------- */}
      {/* Delete confirm modal                                           */}
      {/* ------------------------------------------------------------- */}
      <Modal
        isOpen={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        title="Delete setting?"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-700">
            The setting{" "}
            <span className="font-mono">{pendingDelete?.key}</span> will
            be permanently removed. Code paths that fall back to env
            vars or hard-coded defaults will pick up those values
            instead; everything else will see "missing".
          </p>
          {deleteError && (
            <Alert variant="error" title="Could not delete">
              {deleteError}
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPendingDelete(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="bg-red-600 hover:bg-red-700 active:bg-red-800"
              onClick={() => void handleConfirmDelete()}
              isLoading={deleteSetting.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* ------------------------------------------------------------- */}
      {/* Reveal modal                                                   */}
      {/* ------------------------------------------------------------- */}
      <Modal
        isOpen={revealing !== null}
        onClose={closeReveal}
        title="Reveal secret value"
        size="lg"
      >
        <div className="space-y-4">
          <Alert variant="warning" title="This reveal is recorded">
            A SETTING_REVEALED entry has been written to system_log
            with your user id. Other admins can see who pulled this
            value and when.
          </Alert>
          {revealing && (
            <p className="text-sm text-neutral-700">
              Cleartext value of{" "}
              <span className="font-mono">{revealing.key}</span>:
            </p>
          )}
          {revealError && (
            <Alert variant="error" title="Could not reveal">
              {revealError}
            </Alert>
          )}
          {revealedValue !== null && (
            <>
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                <code
                  data-testid="reveal-cleartext"
                  className="block break-all font-mono text-sm text-neutral-900"
                >
                  {revealedValue}
                </code>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void handleCopyRevealed();
                  }}
                >
                  <i
                    className="fa-duotone fa-thin fa-copy"
                    aria-hidden="true"
                  />
                  {copyState === "copied"
                    ? "Copied"
                    : copyState === "failed"
                      ? "Copy failed"
                      : "Copy"}
                </Button>
                <Button type="button" onClick={closeReveal}>
                  Close
                </Button>
              </div>
            </>
          )}
          {revealedValue === null && !revealError && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="md" />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminSettingsPage;
